import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Spinner, Text, Badge, Input, MessageBar, MessageBarBody, } from "@fluentui/react-components";
import { ChevronLeftRegular, ChevronRightRegular, ArrowSyncRegular, SendRegular, CheckmarkCircleRegular, } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { useDemo } from "../../../context/DemoContext";
import { getBadgeagesByEmployee, createBadgeage, updateBadgeage, getAllApprobateurs, sendChangeRequestEmail, } from "../../../services/dataService";
const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    d.setHours(0, 0, 0, 0);
    return d;
}
function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}
function toISO(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function getWeekKey(monday) {
    const d = new Date(monday);
    d.setDate(d.getDate() + 3); // Jeudi de la semaine ISO
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + yearStart.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}
function formatDateFR(d) {
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function calcDayHours(e1, s1, e2, s2) {
    let total = 0;
    if (e1 && s1)
        total += timeDiff(e1, s1);
    if (e2 && s2)
        total += timeDiff(e2, s2);
    if (total === 0)
        return "";
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${h}:${String(m).padStart(2, "0")}`;
}
function timeDiff(start, end) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    return (eh * 60 + em) - (sh * 60 + sm);
}
function totalWeekMinutes(days) {
    let total = 0;
    for (const d of days) {
        if (d.entree1 && d.sortie1)
            total += timeDiff(d.entree1, d.sortie1);
        if (d.entree2 && d.sortie2)
            total += timeDiff(d.entree2, d.sortie2);
    }
    return total;
}
export const MyBadgeage = () => {
    const { apiToken, graphToken, userEmail, userName } = useAuth();
    const { demoUser } = useDemo();
    const effectiveEmail = demoUser?.email || userEmail;
    const effectiveName = demoUser?.name || userName;
    const [monday, setMonday] = useState(() => getMonday(new Date()));
    const [records, setRecords] = useState([]);
    const [approbateurs, setApprobateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [days, setDays] = useState([]);
    const semaine = getWeekKey(monday);
    const showMessage = (msg, intent) => {
        if (intent === "success") {
            setSuccess(msg);
            setError(null);
        }
        else {
            setError(msg);
            setSuccess(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    };
    const fetchData = useCallback(async () => {
        if (!apiToken)
            return;
        setLoading(true);
        try {
            const [recs, approbs] = await Promise.all([
                getBadgeagesByEmployee(apiToken, effectiveEmail, semaine),
                getAllApprobateurs(apiToken),
            ]);
            setRecords(recs);
            setApprobateurs(approbs);
            const newDays = [];
            for (let i = 0; i < 7; i++) {
                const date = addDays(monday, i);
                const iso = toISO(date);
                const rec = recs.find((r) => r.dateJour.startsWith(iso));
                const isKelio = rec?.source === "Kelio";
                newDays.push({
                    date,
                    iso,
                    record: rec || null,
                    entree1: rec?.entree1 || (i < 5 && !isKelio ? "08:00" : ""),
                    sortie1: rec?.sortie1 || (i < 5 && !isKelio ? "12:00" : ""),
                    entree2: rec?.entree2 || (i < 5 && !isKelio ? "13:00" : ""),
                    sortie2: rec?.sortie2 || (i < 5 && !isKelio ? "17:00" : ""),
                    commentaire: rec?.commentaire || "",
                    isKelio,
                });
            }
            setDays(newDays);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [apiToken, effectiveEmail, semaine, monday]);
    useEffect(() => { fetchData(); }, [fetchData]);
    // Statut de la semaine : Kelio si tous les jours sont Kelio
    const hasKelio = days.some((d) => d.isKelio);
    const allKelio = days.filter((d) => d.entree1 || d.sortie1).every((d) => d.isKelio);
    const weekStatut = useMemo(() => {
        if (records.length === 0)
            return "Brouillon";
        const statuts = new Set(records.map((r) => r.statut));
        if (statuts.has("Soumis"))
            return "Soumis";
        if (statuts.has("Valide") && statuts.size === 1)
            return "Valide";
        if (statuts.has("Rejete"))
            return "Rejete";
        return "Brouillon";
    }, [records]);
    const isEditable = !allKelio && (weekStatut === "Brouillon" || weekStatut === "Rejete");
    const updateDay = (idx, field, value) => {
        if (days[idx].isKelio)
            return;
        setDays((prev) => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
    };
    const handleSave = async () => {
        if (!apiToken)
            return;
        setSaving(true);
        try {
            for (const day of days) {
                if (day.isKelio)
                    continue; // Ne pas toucher aux badgeages Kelio
                const fields = {
                    EmployeeEmail: effectiveEmail,
                    EmployeeName: effectiveName || effectiveEmail,
                    Semaine: semaine,
                    DateJour: day.iso,
                    Entree1: day.entree1,
                    Sortie1: day.sortie1,
                    Entree2: day.entree2,
                    Sortie2: day.sortie2,
                    Commentaire: day.commentaire,
                    Statut: "Brouillon",
                };
                if (day.record) {
                    await updateBadgeage(apiToken, day.record.id, fields);
                }
                else if (day.entree1 || day.sortie1 || day.entree2 || day.sortie2) {
                    const id = await createBadgeage(apiToken || "", fields);
                    day.record = { id, employeeEmail: effectiveEmail, employeeName: effectiveName || "", semaine, dateJour: day.iso, entree1: day.entree1, sortie1: day.sortie1, entree2: day.entree2, sortie2: day.sortie2, commentaire: day.commentaire, statut: "Brouillon", valideurEmail: "", valideurName: "", dateValidation: "", source: "", createdAt: "" };
                }
            }
            showMessage("Horaires enregistrés (brouillon)", "success");
        }
        catch (err) {
            showMessage(`Erreur : ${err instanceof Error ? err.message : "Erreur"}`, "error");
        }
        finally {
            setSaving(false);
        }
    };
    const handleSubmit = async () => {
        if (!apiToken)
            return;
        setSaving(true);
        try {
            await handleSave();
            const updated = await getBadgeagesByEmployee(apiToken || "", effectiveEmail, semaine);
            for (const rec of updated) {
                if (rec.source === "Kelio")
                    continue; // Ne pas soumettre les Kelio
                await updateBadgeage(apiToken || "", rec.id, { Statut: "Soumis" });
            }
            const n1 = approbateurs.find((a) => a.role === "N+1");
            if (n1?.employeeEmail) {
                try {
                    const html = `
            <div style="font-family:Segoe UI,Arial,sans-serif">
              <h3 style="color:#0066cc">Badgeage à valider</h3>
              <p><strong>${effectiveName}</strong> (${effectiveEmail}) a soumis ses horaires de la <strong>${semaine}</strong>.</p>
              <p>Connectez-vous au portail RH pour valider.</p>
            </div>
          `;
                    await sendChangeRequestEmail(graphToken || "", n1.employeeEmail, `[Badgeage] ${effectiveName} — ${semaine}`, html);
                }
                catch { /* email best effort */ }
            }
            showMessage(`Semaine ${semaine} soumise pour validation${n1 ? ` — notification envoyée à ${n1.employeeName}` : ""}`, "success");
            fetchData();
        }
        catch (err) {
            showMessage(`Erreur : ${err instanceof Error ? err.message : "Erreur"}`, "error");
        }
        finally {
            setSaving(false);
        }
    };
    const totalMin = totalWeekMinutes(days);
    const totalH = Math.floor(totalMin / 60);
    const totalM = totalMin % 60;
    if (loading)
        return _jsx(Spinner, { label: "Chargement..." });
    return (_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }, children: [_jsx("h1", { style: { margin: 0 }, children: "Mes badgeages" }), _jsxs("div", { style: { display: "flex", gap: "8px", alignItems: "center" }, children: [hasKelio && (_jsx(Badge, { appearance: "tint", color: "brand", style: { fontSize: "11px" }, children: "Badgeuse" })), _jsx(Badge, { appearance: "filled", color: weekStatut === "Valide" ? "success" : weekStatut === "Soumis" ? "warning" : weekStatut === "Rejete" ? "danger" : "informative", children: weekStatut }), _jsx(Button, { icon: _jsx(ArrowSyncRegular, {}), appearance: "subtle", size: "small", onClick: fetchData })] })] }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "8px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "8px" }, children: _jsx(MessageBarBody, { children: success }) }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }, children: [_jsx(Button, { icon: _jsx(ChevronLeftRegular, {}), appearance: "subtle", size: "small", onClick: () => setMonday(addDays(monday, -7)) }), _jsxs(Text, { size: 400, weight: "bold", children: ["Semaine ", semaine, " \u2014 du ", formatDateFR(monday), " au ", formatDateFR(addDays(monday, 6))] }), _jsx(Button, { icon: _jsx(ChevronRightRegular, {}), appearance: "subtle", size: "small", onClick: () => setMonday(addDays(monday, 7)) })] }), allKelio && (_jsx(MessageBar, { intent: "info", style: { marginBottom: "12px" }, children: _jsx(MessageBarBody, { children: "Les horaires de cette semaine proviennent de la badgeuse. Ils ne sont pas modifiables." }) })), _jsx("div", { style: { overflowX: "auto" }, children: _jsxs("table", { style: { borderCollapse: "collapse", width: "100%", maxWidth: "950px", fontSize: "13px" }, children: [_jsx("thead", { children: _jsxs("tr", { style: { borderBottom: "2px solid #0066cc" }, children: [_jsx("th", { style: { padding: "8px", textAlign: "left", width: "130px" }, children: "Jour" }), _jsx("th", { style: { padding: "8px", textAlign: "center", color: "#0066cc" }, children: "Arriv\u00E9e AM" }), _jsx("th", { style: { padding: "8px", textAlign: "center", color: "#0066cc" }, children: "D\u00E9part AM" }), _jsx("th", { style: { padding: "8px", textAlign: "center", color: "#0066cc" }, children: "Arriv\u00E9e PM" }), _jsx("th", { style: { padding: "8px", textAlign: "center", color: "#0066cc" }, children: "D\u00E9part PM" }), _jsx("th", { style: { padding: "8px", textAlign: "center" }, children: "Total" }), _jsx("th", { style: { padding: "8px", textAlign: "center", width: "60px" }, children: "Source" }), _jsx("th", { style: { padding: "8px", textAlign: "left" }, children: "Commentaire" })] }) }), _jsx("tbody", { children: days.map((day, idx) => {
                                const isWeekend = idx >= 5;
                                const dayHours = calcDayHours(day.entree1, day.sortie1, day.entree2, day.sortie2);
                                const rowBg = day.isKelio ? "#f0f7ff" : isWeekend ? "#f9fafb" : "#fff";
                                return (_jsxs("tr", { style: { borderBottom: "1px solid #e5e7eb", background: rowBg }, children: [_jsxs("td", { style: { padding: "8px" }, children: [_jsx(Text, { weight: "semibold", children: JOURS[idx] }), _jsx("br", {}), _jsx(Text, { size: 200, style: { color: "#666" }, children: formatDateFR(day.date) })] }), ["entree1", "sortie1", "entree2", "sortie2"].map((field) => (_jsx("td", { style: { padding: "4px 8px", textAlign: "center" }, children: day.isKelio ? (_jsx(Text, { size: 300, weight: "semibold", style: { color: day[field] ? "#0066cc" : "#ccc" }, children: day[field] || "—" })) : (_jsx(Input, { type: "time", value: day[field], onChange: (_, d) => updateDay(idx, field, d.value), disabled: !isEditable, style: { width: "90px", textAlign: "center" }, size: "small" })) }, field))), _jsx("td", { style: { padding: "8px", textAlign: "center", fontWeight: 600, color: dayHours ? "#111" : "#ccc" }, children: dayHours || "—" }), _jsx("td", { style: { padding: "4px 8px", textAlign: "center" }, children: day.isKelio ? (_jsx(Badge, { appearance: "tint", color: "brand", size: "small", children: "Badgeuse" })) : day.record ? (_jsx(Badge, { appearance: "tint", color: "informative", size: "small", children: "D\u00E9claratif" })) : null }), _jsx("td", { style: { padding: "4px 8px" }, children: day.isKelio ? (_jsx(Text, { size: 200, style: { color: "#666", fontStyle: "italic" }, children: day.commentaire || "Import Kelio" })) : (_jsx(Input, { value: day.commentaire, onChange: (_, d) => updateDay(idx, "commentaire", d.value), disabled: !isEditable, placeholder: "...", size: "small", style: { width: "100%" } })) })] }, idx));
                            }) }), _jsx("tfoot", { children: _jsxs("tr", { style: { borderTop: "2px solid #0066cc", background: "#f0f7ff" }, children: [_jsx("td", { colSpan: 5, style: { padding: "8px", fontWeight: 700, textAlign: "right" }, children: "Cumul hebdomadaire" }), _jsxs("td", { style: { padding: "8px", textAlign: "center", fontWeight: 700, fontSize: "16px", color: "#0066cc" }, children: [totalH, ":", String(totalM).padStart(2, "0")] }), _jsx("td", { colSpan: 2 })] }) })] }) }), isEditable && !allKelio && (_jsxs("div", { style: { display: "flex", gap: "12px", marginTop: "16px" }, children: [_jsx(Button, { icon: _jsx(CheckmarkCircleRegular, {}), appearance: "secondary", onClick: handleSave, disabled: saving, children: saving ? "Enregistrement..." : "Enregistrer (brouillon)" }), _jsx(Button, { icon: _jsx(SendRegular, {}), appearance: "primary", onClick: handleSubmit, disabled: saving, children: saving ? "Envoi..." : "Soumettre la semaine au N+1" })] })), allKelio && (_jsx(Text, { size: 300, style: { color: "#0066cc", marginTop: "16px", display: "block" }, children: "Vos horaires sont enregistr\u00E9s automatiquement par la badgeuse." })), weekStatut === "Soumis" && !allKelio && (_jsx(Text, { size: 300, style: { color: "#d97706", marginTop: "16px", display: "block" }, children: "Cette semaine a \u00E9t\u00E9 soumise et est en attente de validation par votre responsable." })), weekStatut === "Valide" && !allKelio && (_jsx(Text, { size: 300, style: { color: "#16a34a", marginTop: "16px", display: "block" }, children: "Cette semaine a \u00E9t\u00E9 valid\u00E9e." })), weekStatut === "Rejete" && (_jsx(Text, { size: 300, style: { color: "#dc2626", marginTop: "16px", display: "block" }, children: "Cette semaine a \u00E9t\u00E9 rejet\u00E9e. Vous pouvez corriger et resoumettre." }))] }));
};
